/**
 * Recursively converts null values to undefined.
 * Sanity returns null for optional fields, but TypeScript expects undefined.
 *
 * @param value - The value to convert (can be any type)
 * @returns The value with all nulls converted to undefined
 */
export const nullToUndefined = <T>(value: T | null): T | undefined =>
  value === null ? undefined : value;

/**
 * Converts null to undefined and recursively converts all nested nulls.
 * This is a convenience function that handles both top-level and nested nulls.
 */
export const sanitizeSanityValue = <T>(value: T | null): T | undefined => {
  if (value === null) return undefined;
  return convertNullsToUndefined(value);
};

/**
 * Recursively converts all null values in an object to undefined.
 * This handles nested objects and arrays from Sanity data.
 *
 * @param value - The value to convert (can be null)
 * @returns The value with all nulls recursively converted to undefined, or undefined if value is null
 */
export const convertNullsToUndefined = <T>(value: T | null): T | undefined => {
  if (value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.map(convertNullsToUndefined) as T;
  }

  if (typeof value === 'object' && value !== null) {
    const converted = {} as T;
    let hasAnyValue = false;
    for (const [key, val] of Object.entries(value)) {
      const convertedVal = convertNullsToUndefined(val);
      if (convertedVal !== undefined) {
        hasAnyValue = true;
        (converted as Record<string, unknown>)[key] = convertedVal;
      }
    }
    // If all values are undefined, return undefined instead of empty object
    if (!hasAnyValue) {
      return undefined;
    }
    return converted;
  }

  return value;
};
